// Turns a Business Loan / GP Loan summary schedule (buildMonthlyLoanSummary in
// utils.js) into the payment rows of the Account Statement. Both statements use
// this, so they cannot drift apart. Every rupee received appears once: the
// "Decreased By" column adds up to the summary's totalEmiPaid.
import { toIstDateOnly } from "./loanAllocation";

// Bootstrap row classes for each kind of row.
export const STATEMENT_ROW_CLASS = {
  payment: "",
  carried: "table-light",
  due: "text-muted fst-italic",
  excess: "table-warning",
};

const rupees =(n) => `Rs.${Math.round(n).toLocaleString("en-IN")}`;
const method = (receipt) => receipt?.paymentMethod || "Cash";
const moneyDate = (receipt) => toIstDateOnly(receipt?.collectedDate) || toIstDateOnly(receipt?.emiDate);

const instalmentNote = (inst) => {
  if (inst.status === "Paid") return `Instalment ${inst.number} paid`;
  const due = inst.interestCharge + inst.principalCharge - inst.totalReceivedAmount;
  return `Instalment ${inst.number} part-paid, ${rupees(due)} still due`;
};

// formatDate turns a YYYY-MM-DD date into the statement's display format.
// isSettled: a closed loan's later instalments were settled by foreclosure, not missed.
export const buildStatementPaymentRows = (schedule, formatDate = (d) => d, { isSettled = false } = {}) => {
  const rows = [];
  (schedule || []).forEach((inst, index) => {
    if (inst.isAdvance) {
      const receipt = inst.receipts?.[0];
      rows.push({
        key: `excess-${index}`,
        kind: "excess",
        collectionDate: moneyDate(receipt),
        scheduledDate: null,
        description: inst.carriedForward
          ? `Balance of receipt dated ${formatDate(moneyDate(receipt))} kept for the next instalment`
          : `Advance Payment Received (${method(receipt)}) - kept for the next instalment`,
        interest: null,
        principal: null,
        decreasedBy: inst.totalReceivedAmount,
        note: null,
      });
      return;
    }

    const allocations = inst.allocations || [];
    if (allocations.length === 0) {
      rows.push({
        key: `due-${index}`,
        kind: "due",
        collectionDate: null,
        scheduledDate: inst.emiDate,
        description: `EMI Due (Instalment ${inst.number})`,
        interest: null,
        principal: null,
        decreasedBy: null,
        note: inst.isDue && !isSettled ? `${rupees(inst.interestCharge + inst.principalCharge)} not paid` : null,
      });
      return;
    }

    allocations.forEach((a, aIndex) => {
      const date = moneyDate(a.receipt);
      rows.push({
        key: `paid-${index}-${aIndex}`,
        kind: a.carriedForward ? "carried" : "payment",
        collectionDate: date,
        scheduledDate: inst.emiDate,
        description: a.carriedForward
          ? `Carried forward from receipt dated ${formatDate(date)} (${method(a.receipt)})`
          : `EMI Payment Received (${method(a.receipt)})`,
        interest: a.interest,
        principal: a.principal,
        decreasedBy: a.interest + a.principal,
        note: aIndex === allocations.length - 1 ? instalmentNote(inst) : null,
      });
    });
  });
  return rows;
};
